import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import apiService from '../services/ApiService';
import { useAuth } from '../contexts/AuthContext';
import { AddVehicleModal } from './AddVehicleModal';
import EditVehicleModal from './EditVehicleModal';
import { EditProfileModal } from './EditProfileModal';
import { LoginModal } from './LoginModal';
import { RegisterModal } from './RegisterModal';
import VehicleCard from './VehicleCard';
import Footer from './Footer';
import SafetyActions from './safety/SafetyActions';
import { VehicleCardSkeleton } from './ui/Skeletons';
import EmptyState, { CarIcon } from './ui/EmptyState';
import PageBackLink from './ui/PageBackLink';
import Button from './ui/Button';
import { AvatarImage } from './ui/AvatarImage';
import VehicleSectionNav from './vehicle/VehicleSectionNav';
import { canEditVehicle } from '../utils/permissions';
import { vehicleProfilePath, resolveVehicleNavSlug } from '../utils/vehicleUrls';
import { updateMetaTags, injectJsonLd } from '../utils/metaTags';
import { buildProfileSeo, type ProfileSeoPayload } from '../utils/profileSeo';
import { defaultSiteGraph, profileJsonLd } from '../utils/structuredData';

function safeHttpUrl(value?: string | null): string | null {
  if (!value || typeof value !== 'string') return null;
  try {
    const url = new URL(value);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    return url.toString();
  } catch {
    return null;
  }
}

interface UserProfileData {
  user: {
    id: number;
    username: string;
    first_name: string;
    last_name: string;
    display_name: string;
    created_at: string;
    vehicle_count: number;
    modification_count: number;
    avatar_url?: string;
    avatar_position?: { x: number; y: number; scale: number };
    instagram_username?: string;
    facebook_url?: string;
    website_url?: string;
    bio?: string;
    is_managed_profile?: boolean;
  };
  vehicles: Array<{
    id: number;
    year: number;
    make: string;
    model: string;
    trim: string;
    color?: string;
    nickname?: string;
    description?: string;
    modification_count: number;
    last_modification?: string;
    display_name: string;
    is_primary: boolean;
    is_public: boolean;
    created_at: string;
    image_url?: string;
    // Performance data
    horsepower?: number;
    torque?: number;
    redline?: number;
    // Settings for privacy
    settings?: {
      show_pricing: boolean;
      show_performance: boolean;
    };
    // Total investment
    total_investment?: number;
    // Like data
    like_count?: number;
    user_has_liked?: boolean;
  }>;
  seo?: ProfileSeoPayload;
}

const UserProfile: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, user: currentUser } = useAuth();
  const [profileData, setProfileData] = useState<UserProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddVehicleModalOpen, setIsAddVehicleModalOpen] = useState(false);
  const [isEditVehicleModalOpen, setIsEditVehicleModalOpen] = useState(false);
  const [isEditProfileModalOpen, setIsEditProfileModalOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<any>(null);
  const [isOwner, setIsOwner] = useState(false);

  useEffect(() => {
    const loadUserProfile = async () => {
      if (!username) {
        setError('Username is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Check if viewing own profile
        const isViewingOwnProfile = isAuthenticated && currentUser?.username === username;
        setIsOwner(isViewingOwnProfile);

        if (isViewingOwnProfile) {
          const [vehicles, freshUserData, publicProfile] = await Promise.all([
            apiService.getUserVehicles(),
            apiService.getUserProfile(),
            apiService.getPublicUserProfile(username).catch(() => null),
          ]);
          
          // Fetch first hero image for each vehicle
          const vehiclesWithImages = await Promise.all(
            vehicles.map(async (vehicle: any) => {
              try {
                const images = await apiService.getVehicleImages(vehicle.id);
                const heroImages = images
                  .filter(img => img.image_type === 'hero')
                  .sort((a, b) => a.sort_order - b.sort_order);
                
                return {
                  ...vehicle,
                  display_name: vehicle.nickname || `${vehicle.year} ${vehicle.make} ${vehicle.model} ${vehicle.trim}`,
                  image_url: heroImages.length > 0 ? heroImages[0].image_url : undefined
                };
              } catch (imageError) {
                console.error(`Failed to load images for vehicle ${vehicle.id}:`, imageError);
                return {
                  ...vehicle,
                  display_name: vehicle.nickname || `${vehicle.year} ${vehicle.make} ${vehicle.model} ${vehicle.trim}`
                };
              }
            })
          );
          
          const data = {
            user: {
              id: freshUserData.id,
              username: freshUserData.username,
              first_name: freshUserData.first_name,
              last_name: freshUserData.last_name,
              display_name: `${freshUserData.first_name} ${freshUserData.last_name}`.trim() || freshUserData.username,
              created_at: freshUserData.created_at || '',
              vehicle_count: vehiclesWithImages.length,
              modification_count: vehiclesWithImages.reduce((sum, v) => sum + (v.modification_count || 0), 0),
              avatar_url: freshUserData.avatar_url,
              avatar_position: freshUserData.avatar_position,
              instagram_username: freshUserData.instagram_username,
              facebook_url: freshUserData.facebook_url,
              website_url: freshUserData.website_url,
              bio: freshUserData.bio
            },
            vehicles: vehiclesWithImages,
            seo: publicProfile?.seo,
          };
          setProfileData(data);
        } else {
          // Load public profile data (now includes fresh avatar data from server)
          const data = await apiService.getPublicUserProfile(username);
          
          // Fetch first hero image for each vehicle in public profile
          if (data.vehicles) {
            const vehiclesWithImages = await Promise.all(
              data.vehicles.map(async (vehicle: any) => {
                try {
                  const images = await apiService.getVehicleImages(vehicle.id);
                  const heroImages = images
                    .filter(img => img.image_type === 'hero')
                    .sort((a, b) => a.sort_order - b.sort_order);
                  
                  return {
                    ...vehicle,
                    image_url: heroImages.length > 0 ? heroImages[0].image_url : undefined
                  };
                } catch (imageError) {
                  console.error(`Failed to load images for vehicle ${vehicle.id}:`, imageError);
                  return vehicle;
                }
              })
            );
            
            data.vehicles = vehiclesWithImages;
          }
          
          setProfileData(data);
        }
      } catch (err: any) {
        console.error('Failed to load user profile:', err);
        setError(err.message || 'Failed to load user profile');
      } finally {
        setLoading(false);
      }
    };

    loadUserProfile();
  }, [username, isAuthenticated, currentUser]);

  useEffect(() => {
    if (!profileData || !username) return;

    const origin = window.location.origin;
    const seo =
      profileData.seo ??
      buildProfileSeo({
        origin,
        username,
        displayName: profileData.user.display_name,
        bio: profileData.user.bio,
        avatarUrl: profileData.user.avatar_url,
        vehicles: profileData.vehicles,
      });

    updateMetaTags({
      title: seo.title,
      description: seo.description,
      canonical: seo.canonical,
      url: seo.canonical,
      image: seo.image,
      robots: seo.robots,
      imageAlt: `${profileData.user.display_name} on Bilt:Spec`,
    });

    if (seo.indexable) {
      injectJsonLd([
        ...defaultSiteGraph(origin),
        profileJsonLd({
          origin,
          url: seo.canonical,
          title: seo.title,
          description: seo.description,
          displayName: profileData.user.display_name,
          username,
          image: seo.image,
        }),
      ]);
    }
  }, [profileData, username]);

  useEffect(() => {
    if (!isOwner || searchParams.get('add') !== '1') return;
    setIsAddVehicleModalOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('add');
    setSearchParams(next, { replace: true });
  }, [isOwner, searchParams, setSearchParams]);

  const handleVehicleClick = (vehicle: any) => {
    if (!username) return;
    const slug = resolveVehicleNavSlug({
      public_slug: vehicle.public_slug,
      nickname: vehicle.nickname || vehicle.display_name,
      year: vehicle.year,
      make: vehicle.make,
      model: vehicle.model,
      trim: vehicle.trim,
    });
    if (!slug) return;
    navigate(vehicleProfilePath(username, slug));
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  const handleAddVehicle = () => {
    setIsAddVehicleModalOpen(true);
  };

  const handleVehicleAdded = (newVehicle: any) => {
    // Refresh the profile data to include the new vehicle
    if (profileData) {
      const updatedVehicles = [...profileData.vehicles, {
        ...newVehicle,
        display_name: newVehicle.nickname || `${newVehicle.year} ${newVehicle.make} ${newVehicle.model} ${newVehicle.trim}`,
        modification_count: 0
      }];

      setProfileData({
        ...profileData,
        vehicles: updatedVehicles,
        user: {
          ...profileData.user,
          vehicle_count: updatedVehicles.length
        }
      });
    }
    setIsAddVehicleModalOpen(false);
    const slug = newVehicle.public_slug || newVehicle.slug || resolveVehicleNavSlug(newVehicle);
    if (slug && username) navigate(`${vehicleProfilePath(username, slug)}?addPart=1&view=list#build`);
  };

  const handleEditVehicle = (vehicle: any) => {
    setSelectedVehicle({
      ...vehicle,
      owner_username: vehicle.owner_username || username || currentUser?.username,
    });
    setIsEditVehicleModalOpen(true);
  };

  const handleVehicleUpdated = (updatedVehicle: any) => {
    if (profileData) {
      const updatedVehicles = profileData.vehicles.map(v =>
        v.id === updatedVehicle.id ? {
          ...updatedVehicle,
          display_name: updatedVehicle.nickname || `${updatedVehicle.year} ${updatedVehicle.make} ${updatedVehicle.model} ${updatedVehicle.trim}`
        } : v
      );

      setProfileData({
        ...profileData,
        vehicles: updatedVehicles
      });
    }
    setIsEditVehicleModalOpen(false);
    setSelectedVehicle(null);
  };

  const handleEditProfile = () => {
    setIsEditProfileModalOpen(true);
  };

  const handleProfileUpdated = (updatedProfile: any) => {
    // Update the displayed user data
    if (profileData) {
      setProfileData(prev => ({
        ...prev!,
        user: {
          ...prev!.user,
          first_name: updatedProfile.first_name,
          last_name: updatedProfile.last_name,
          display_name: updatedProfile.first_name && updatedProfile.last_name 
            ? `${updatedProfile.first_name} ${updatedProfile.last_name}`
            : prev!.user.username,
          avatar_url: updatedProfile.avatar_url,
          avatar_position: updatedProfile.avatar_position,
          facebook_url: updatedProfile.facebook_url,
          website_url: updatedProfile.website_url,
          bio: updatedProfile.bio
        }
      }));
    }
    
    // Note: AuthContext will handle updating the current user data automatically
  };

  const handleSwitchToRegister = () => {
    setIsLoginModalOpen(false);
    setIsRegisterModalOpen(true);
  };

  const handleSwitchToLogin = () => {
    setIsRegisterModalOpen(false);
    setIsLoginModalOpen(true);
  };

  const handleLoginSuccess = () => {
    setIsLoginModalOpen(false);
    // The useEffect will automatically reload the profile data when isAuthenticated changes
  };

  const handleRegisterSuccess = () => {
    setIsRegisterModalOpen(false);
    // The useEffect will automatically reload the profile data when isAuthenticated changes
  };

  if (loading) {
    return (
      <>
        <main className="page max-w-7xl mx-auto px-4 sm:px-6 py-10" role="status" aria-busy="true">
          <span className="sr-only">Loading profile…</span>
          <div className="h-40 rounded-2xl bg-white/[0.04] mb-8" aria-hidden />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[0, 1, 2].map((i) => <VehicleCardSkeleton key={i} />)}
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <div className="page flex flex-col">
        <div className="flex-1 flex items-center justify-center px-4">
          <EmptyState
            title="Profile not found"
            description={error}
            action={
              <Button type="button" variant="primary" className="min-h-[44px]" onClick={handleBackToHome}>
                Back to Bilt:Spec
              </Button>
            }
          />
        </div>
        <Footer />
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="page flex flex-col">
        <div className="flex-1 flex items-center justify-center px-4">
          <EmptyState
            title="User not found"
            description="This profile may have been removed or the username changed."
            action={
              <Button type="button" variant="primary" className="min-h-[44px]" onClick={handleBackToHome}>
                Back to Bilt:Spec
              </Button>
            }
          />
        </div>
        <Footer />
      </div>
    );
  }

  const { user, vehicles } = profileData;
  const facebookUrl = safeHttpUrl(user.facebook_url);
  const websiteUrl = safeHttpUrl(user.website_url);

  const ownerTools = isOwner
    ? [
        {
          id: 'edit-profile',
          label: 'Edit profile',
          description: 'Name, email, location, and the rest of your account details',
          onClick: handleEditProfile,
        },
        {
          id: 'add-vehicle',
          label: 'Add a Build',
          description: 'Start a new vehicle in your garage',
          onClick: handleAddVehicle,
        },
      ]
    : [];

  const profileBody = (
    <>
      {isOwner ? (
        <PageBackLink label="Back to Home" to="/" className="mb-6" />
      ) : null}
      <div id="profile">
        <div className="hidden md:flex items-center space-x-6 mb-8">
          {user.avatar_url ? (
            <AvatarImage
              src={user.avatar_url}
              alt={`${user.display_name}'s avatar`}
              className="w-24 h-24 border border-white/[0.08]"
              position={user.avatar_position}
            />
          ) : (
            <div className="w-24 h-24 bg-gradient-brand-tile rounded-full flex items-center justify-center text-2xl font-bold text-white">
              {user.display_name.charAt(0).toUpperCase()}
            </div>
          )}
          <div>
            <p className="kicker mb-2">{user.is_managed_profile ? 'Managed by Bilt · Unclaimed' : 'Member'}</p>
            <h1 className="heading-display tracking-tight mb-2">
              {user.display_name}
              {isOwner && <span className="text-lg font-sans font-normal text-gray-400 ml-2">(You)</span>}
            </h1>
            <p className="text-gray-400 text-base mb-2 flex items-center gap-1">
              <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
              </svg>
              <span>@{user.username}</span>
            </p>
            {user.instagram_username && (
              <p className="text-gray-400 text-base mb-2">
                <a 
                  href={`https://instagram.com/${user.instagram_username}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 hover:text-orange-400 transition-colors"
                >
                  <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                  <span>@{user.instagram_username}</span>
                </a>
              </p>
            )}
            {facebookUrl && (
              <p className="text-gray-400 text-base mb-2">
                <a 
                  href={facebookUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 hover:text-orange-400 transition-colors"
                >
                  <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  <span>Facebook</span>
                </a>
              </p>
            )}
            {websiteUrl && (
              <p className="text-gray-400 text-base mb-2">
                <a href={websiteUrl} target="_blank" rel="noopener noreferrer" className="hover:text-orange-400 transition-colors">
                  {new URL(websiteUrl).hostname.replace(/^www\./, '')}
                </a>
              </p>
            )}
            {user.bio && (
              <p className="text-gray-400 text-base mb-2">{user.bio}</p>
            )}
            {user.is_managed_profile && (
              <p className="text-sm text-gray-400 mb-3">This profile is maintained by Bilt and has not been claimed by the builder.</p>
            )}
            <div className="flex space-x-6 text-sm text-gray-400">
              <span><strong className="text-white">{user.vehicle_count}</strong> vehicles</span>
              <span><strong className="text-white">{user.modification_count}</strong> modifications</span>
              {user.created_at && !user.is_managed_profile && (
                <span>Member since {new Date(user.created_at).getFullYear()}</span>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="md:hidden mb-8">
          <div className="flex items-center space-x-4 mb-4">
            {user.avatar_url ? (
              <AvatarImage
                src={user.avatar_url}
                alt={`${user.display_name}'s avatar`}
                className="w-20 h-20 border border-white/[0.08]"
                position={user.avatar_position}
              />
            ) : (
              <div className="w-20 h-20 bg-gradient-brand-tile rounded-full flex items-center justify-center text-xl font-bold text-white">
                {user.display_name.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="flex-1">
              <p className="kicker mb-1">{user.is_managed_profile ? 'Managed by Bilt · Unclaimed' : 'Member'}</p>
              <h1 className="heading-display tracking-tight text-2xl mb-1">
                {user.display_name}
                {isOwner && <span className="text-sm text-gray-400 ml-2">(You)</span>}
              </h1>
              <p className="text-gray-400 text-base mb-2 flex items-center gap-1">
                <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                </svg>
                <span>@{user.username}</span>
              </p>
              {user.instagram_username && (
                <p className="text-gray-400 text-base mb-2">
                  <a 
                    href={`https://instagram.com/${user.instagram_username}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 hover:text-orange-400 transition-colors"
                  >
                    <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                    <span>@{user.instagram_username}</span>
                  </a>
                </p>
              )}
              {facebookUrl && (
                <p className="text-gray-400 text-base mb-2">
                  <a 
                    href={facebookUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 hover:text-orange-400 transition-colors"
                  >
                    <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                    <span>Facebook</span>
                  </a>
                </p>
              )}
              {websiteUrl && (
                <p className="text-gray-400 text-base mb-2">
                  <a href={websiteUrl} target="_blank" rel="noopener noreferrer" className="hover:text-orange-400 transition-colors">
                    {new URL(websiteUrl).hostname.replace(/^www\./, '')}
                  </a>
                </p>
              )}
              {user.bio && (
                <p className="text-gray-400 text-base mb-2">{user.bio}</p>
              )}
              {user.is_managed_profile && (
                <p className="text-sm text-gray-400 mb-3">This profile is maintained by Bilt and has not been claimed by the builder.</p>
              )}
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-400">
                <span><strong className="text-white">{user.vehicle_count}</strong> vehicles</span>
                <span><strong className="text-white">{user.modification_count}</strong> modifications</span>
                {user.created_at && !user.is_managed_profile && (
                  <span>Member since {new Date(user.created_at).getFullYear()}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div id="builds">
          <div className="flex items-center justify-between mb-6">
            <h2 className="heading-section">
              {isOwner ? 'Your builds' : 'Public builds'}
            </h2>
            {isOwner && vehicles.length > 0 && (
              <Button variant="primary" onClick={handleAddVehicle} data-testid="garage-add-vehicle">
                Add a Build
              </Button>
            )}
          </div>

          {vehicles.length === 0 ? (
            <EmptyState
              imageSrc={isOwner ? '/images/brand/empty-garage-bay.jpg' : undefined}
              imageAlt=""
              icon={isOwner ? undefined : <CarIcon />}
              title={isOwner ? 'Your Garage is ready when you are' : 'No public builds'}
              description={
                isOwner
                  ? 'Collect cars in Passport, or add a car you own or manage here. You can enjoy Bilt without building a car.'
                  : 'No public builds have been shared on this profile yet.'
              }
              action={
                isOwner ? (
                  <Button variant="primary" onClick={handleAddVehicle} data-testid="garage-add-vehicle">
                    Add a Build
                  </Button>
                ) : (
                  <Button variant="primary" onClick={() => navigate('/register')}>
                    Join Bilt
                  </Button>
                )
              }
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
              {vehicles.map((vehicle) => {
                // Check if current user can edit this vehicle (owner or super admin)
                const canEdit = currentUser ? canEditVehicle(currentUser, vehicle) : false;
                
                return (
                  <VehicleCard
                    key={vehicle.id}
                    vehicle={vehicle}
                    onVehicleClick={handleVehicleClick}
                    showOwner={false}
                    isOwner={canEdit}
                    onEditClick={handleEditVehicle}
                    collectionMode
                    discoveryBadges={(vehicle as any).badges}
                    discoveryBadgeOverflow={(vehicle as any).badge_overflow || 0}
                    limitedDesignationPrimary={(vehicle as any).limited_designation_primary || null}
                  />
                );
              })}
            </div>
          )}
      </div>
    </>
  );

  return (
    <div className="page duo-canvas">
      <div className={isOwner ? 'page-content' : 'page-content-narrow'}>
        {!isOwner && user && <SafetyActions username={user.username} />}
        {isOwner ? (
          <VehicleSectionNav
            title="Profile"
            mobileTriggerLabel="Profile tools"
            items={[
              { id: 'profile', label: 'Overview' },
              { id: 'builds', label: 'Builds' },
            ]}
            ownerTools={ownerTools}
            ownerToolsLabel="Tools"
            ownerToolsHint="Only you see these"
          >
            {profileBody}
          </VehicleSectionNav>
        ) : (
          profileBody
        )}
      </div>

      {/* Modals */}
      {isAddVehicleModalOpen && (
        <AddVehicleModal
          isOpen={isAddVehicleModalOpen}
          onClose={() => setIsAddVehicleModalOpen(false)}
          onVehicleAdded={handleVehicleAdded}
        />
      )}

      {isEditVehicleModalOpen && selectedVehicle && (
        <EditVehicleModal
          isOpen={isEditVehicleModalOpen}
          onClose={() => {
            setIsEditVehicleModalOpen(false);
            setSelectedVehicle(null);
          }}
          vehicle={selectedVehicle}
          onVehicleUpdated={handleVehicleUpdated}
        />
      )}

      {isEditProfileModalOpen && (
        <EditProfileModal
          isOpen={isEditProfileModalOpen}
          onClose={() => setIsEditProfileModalOpen(false)}
          onProfileUpdated={handleProfileUpdated}
          currentUser={currentUser}
        />
      )}

      {isLoginModalOpen && (
        <LoginModal
          isOpen={isLoginModalOpen}
          onClose={() => setIsLoginModalOpen(false)}
          onLogin={handleLoginSuccess}
          onSwitchToRegister={handleSwitchToRegister}
        />
      )}

      {isRegisterModalOpen && (
        <RegisterModal
          isOpen={isRegisterModalOpen}
          onClose={() => setIsRegisterModalOpen(false)}
          onRegister={handleRegisterSuccess}
          onSwitchToLogin={handleSwitchToLogin}
        />
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default UserProfile;
